/* Who may manage a salon's accounts, and what they may do to them.
 *
 * Pure, and tested, for the reason js/rules/licence.js gives about its own
 * subject: the screen these decisions drive cannot be imported without a
 * Supabase config, and these are decisions that must not be wrong. Two of them
 * would be quietly permanent if they were:
 *
 *   `wouldStrandTenant`  a salon left with no owner can never again add an
 *                        account. The People screen is gone, and so is the
 *                        ability to grant it back.
 *   `canManage`          the whole of who sees that screen.
 *
 * NONE OF THIS IS THE ENFORCEMENT. Every rule here is mirrored in the database —
 * the owner policies and the last-owner trigger in
 * server/schema/09-owner-members.sql — and that is what actually decides. What
 * these are for is saying so in the interface *before* somebody presses the
 * button, so a refusal arrives as a sentence next to the control rather than as
 * a Postgres error in a toast. A check that lives only in a page is a check a
 * mistyped fetch in a console skips entirely; a check that lives only in the
 * database is a button that fails for reasons nobody can read.
 */

export const ROLES = ['owner', 'station'];

/* Is this a role the product knows?
 *
 * The column is free text — part 1 declared it `text not null default
 * 'station'` — so a row can hold anything a service-role script has ever put
 * there. Anything unrecognised is treated as the lesser of the two everywhere
 * below, which is the safe direction: an unknown word must never read as owner.
 */
export function isRole(role) {
  return ROLES.indexOf(role) !== -1;
}

export function isOwner(role) {
  return role === 'owner';
}

// May this person see and use the People screen at all?
export function canManage(role) {
  return isOwner(role);
}

/* What one member's row says, out of the list the screen already has.
 *
 * Returns null for somebody who is not a member, which the caller must treat as
 * "not an owner" rather than as an error — a session whose membership has just
 * been removed by another station is exactly this, and it should lose the
 * screen rather than break it.
 */
export function roleOf(members, userId) {
  if (!userId) return null;
  for (var i = 0; i < members.length; i++) {
    if (members[i].userId === userId) return members[i].role || null;
  }
  return null;
}

export function owners(members) {
  return members.filter(function (m) { return isOwner(m.role); });
}

/* Would this change leave the salon with nobody who can manage it?
 *
 * `nextRole` is the role the member is being moved to, or null to mean removal.
 * The question is only ever about owners: demoting or removing a station is
 * never what strands a salon, however few of them are left.
 *
 * Answered against the list the screen is holding, which can be a moment stale —
 * another owner may have been removed on a second station since it loaded. That
 * is why this is not the enforcement. The trigger asks the same question of the
 * table itself, inside the transaction, and wins.
 */
export function wouldStrandTenant(members, userId, nextRole) {
  var current = roleOf(members, userId);
  if (!isOwner(current)) return false;      // not an owner: nothing to lose
  if (isOwner(nextRole)) return false;      // still an owner afterwards
  return owners(members).length <= 1;
}

/* How many accounts this salon may still create.
 *
 * `seats` is null on an unlimited plan, and null is also what an unknown
 * licence looks like — see js/licensing.js, which fails open on every route
 * where the answer is not known. Both mean the same thing here and must: a
 * salon whose licensing schema is missing, or whose wifi is down, is not a
 * salon that should be told it cannot add a receptionist.
 *
 * Negative is impossible by construction rather than by clamping, because a
 * salon can legitimately be over its limit — lowering a plan's seats below what
 * a salon already has signs nobody out, it stops the next one being attached.
 * So this floors at zero and `seatsFull` reads the same either way.
 */
export function seatsLeft(members, seats) {
  if (seats == null) return null;
  return Math.max(0, seats - members.length);
}

export function seatsFull(members, seats) {
  var left = seatsLeft(members, seats);
  return left !== null && left <= 0;
}

/* Roughly, is this an email address?
 *
 * Deliberately loose. The account is created by Supabase Auth, which is the
 * thing that actually decides, and a stricter pattern here would only mean
 * refusing addresses that work. This exists to catch the empty box and the
 * obvious typo before a round trip, not to adjudicate RFC 5322.
 */
export function looksLikeEmail(value) {
  var v = String(value == null ? '' : value).trim();
  return v.length > 2 && v.indexOf('@') > 0 && v.indexOf('@') < v.length - 1
    && v.indexOf(' ') === -1;
}

/* Why this account cannot be created, or null if it can.
 *
 * One function rather than three checks at the call site, so the screen and the
 * tests agree about precedence: who you are, then whether the address is one,
 * then whether there is room. Permission first because a station being told
 * "no seats left" would be a sentence about the wrong problem.
 */
export function refuseCreate(actorRole, email, members, seats) {
  if (!canManage(actorRole)) {
    return 'Only an owner can add accounts to this salon.';
  }
  if (!looksLikeEmail(email)) {
    return 'That does not look like an email address.';
  }
  var taken = members.some(function (m) {
    return (m.email || '').toLowerCase() === String(email).trim().toLowerCase();
  });
  if (taken) {
    return 'That address is already on this salon.';
  }
  if (seatsFull(members, seats)) {
    return 'This salon’s plan is for ' + seats +
      (seats === 1 ? ' account' : ' accounts') +
      ', and they are all in use. Remove somebody first, or move to a larger plan.';
  }
  return null;
}
